/* eslint-disable react/no-this-in-sfc */
import {LoadingButton} from '@mui/lab';
import {Autocomplete, Box, Grid, MenuItem, TextField} from '@mui/material';
import {Container, Stack, useTheme} from '@mui/system';
import {t} from 'i18next';
import React, {useEffect, useRef, useState} from 'react';

import Iconify from 'src/minimal-components/iconify';
import {BASE_MENU_ITEM, BASE_RECORD_ID} from 'src/routes/BaseRoute';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import {getLMSResultsData} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';
import {useParams, useLocation} from 'react-router-dom';
import EncryptUtils from 'src/utils/EncryptUtils';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Card,
  Typography,
  IconButton,
  FormHelperText
} from '@mui/material';
import RHFAutocomplete from 'src/minimal-components/hook-form/rhf-autocomplete';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import uuidv4 from 'src/utils/uuidv4';

interface LearningObjectiveFormProps {
  tabname?: string;
  formdata?: any;
}
interface Options {
  label: string;
  value: number;
}
interface RequiredOptions {
  label: string;
  value: string;
}
interface SkillSet {
  id: string;
  CourseLearningObjectiveType_id: string;
  rating_out_of_5: number;
}
interface SkillSets {
  id: string;
  CourseLearningObjectiveType_id: string;
  rating_out_of_5: number;
}
interface FormValues {
  PreRequisitId: string | null | undefined;
  CourseOverview_id: string | null | undefined;
  CourseLearningObjectiveType_id: string | null;
  rating_out_of_5: number | null;
  Skills_acquired: SkillSet[] | undefined;
}
const LearningObjectivesForm: React.FC<LearningObjectiveFormProps> = props => {
  const theme = useTheme();
  const params: any = useParams();
  const location = useLocation();
  const sourceSystemOptions = useRef();
  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);
  const menuItem1 = new URLSearchParams(location?.search).get('section');
  const menuItem2 = new URLSearchParams(location?.search).get('subsection');
  const uniqueid = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [uiFieldsData, setUIFieldsData] = useState<any>(props.formdata);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uiData, setUiData] = useState<FormValues>();
  const [dropDownData, setDropDownData] = useState<any[]>([]);
  const {
    appState: {applicationName}
  } = useAppContext();

  const [skillSets, setSkillSets] = useState<SkillSets[]>([]);
  const _roles: Options[] = [
    {label: '1', value: 1},
    {label: '2', value: 2},
    {label: '3', value: 3},
    {label: '4', value: 4},
    {label: '5', value: 5}
  ];
  const init = async () => {
    try {
      setIsPageLoading(true);
      if (props.formdata) {
        setUIFieldsData(props.formdata || {});
        const {sql_data} = props.formdata['AssessmentSetUpForm'];
        const data: any = {
          sql_data: sql_data,
          operation: 'GETQUESTIONFORMOPTIONS'
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        setDropDownData((prevData: any[]) => {
          const resultData = result.data; // Assuming `result.data` contains the JSON object

          const transformedData = Object.keys(resultData).map(key => ({
            Name: key,
            Values: resultData[key].map((item: any) => ({
              label: item.label,
              value: item.value
            }))
          }));

          // Filter out items where `Name` already exists in `prevData`
          const newData = transformedData.filter(
            newItem =>
              !prevData.some(prevItem => prevItem.Name === newItem.Name)
          );

          return [...(prevData || []), ...newData];
        });
        const {sql_datas} = props.formdata['create_sql'];
        const datas: any = {
          sql_data: sql_datas,
          data: {CourseOverview_id: uniqueid, course_overview_id: uniqueid},
          operation: 'SEARCH'
        };
        const encryptRequestDatas = EncryptUtils.encryptedData(datas);
        const results = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestDatas
        });
        const transformedResult = results.data.map((item: any) => {
          return {
            ...item,
            Skills_acquired: item.Skills_acquired
            // Add any additional transformations here if needed
          };
        });
        setUiData(transformedResult[0]);
      } else {
        setUIFieldsData({});
      }
      setIsPageLoading(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsPageLoading(false);
    }
  };
  useEffect(() => {
    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const schema = yup.object({
    CourseLearningObjectiveType_id: yup
      .string()
      .nullable()
      .test(
        'both-or-none',
        'Both CourseLearningObjectiveType_id and rating_out_of_5 must be provided together',
        function (value) {
          const {rating_out_of_5} = this.parent;
          const bothNull = value === null && rating_out_of_5 === null;
          const bothNonNull = value !== null && rating_out_of_5 !== null;
          return bothNull || bothNonNull;
        }
      ),
    rating_out_of_5: yup
      .number()
      .nullable()
      .test(
        'both-or-none',
        'Both CourseLearningObjectiveType_id and rating_out_of_5 must be provided together',
        function (value) {
          const {CourseLearningObjectiveType_id} = this.parent;
          const bothNull =
            value === null && CourseLearningObjectiveType_id === null;
          const bothNonNull =
            value !== null && CourseLearningObjectiveType_id !== null;
          return bothNull || bothNonNull;
        }
      ),
    Skills_acquired: yup.array(
      yup
        .object({
          id: yup.string().required(),
          CourseLearningObjectiveType_id: yup.string().required(),
          rating_out_of_5: yup.number().nullable().required()
        })
        .nullable()
    )
  });

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    setValue,
    formState: {errors}
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      CourseLearningObjectiveType_id:
        uiData?.CourseLearningObjectiveType_id || null,
      rating_out_of_5: uiData?.rating_out_of_5 || null,
      Skills_acquired: uiData?.Skills_acquired || undefined
    }
  });
  useEffect(() => {
    if (uiData) {
      setSkillSets(uiData?.Skills_acquired || []);
      reset({
        CourseLearningObjectiveType_id:
          uiData?.CourseLearningObjectiveType_id || null,
        rating_out_of_5: uiData?.rating_out_of_5 || null,
        Skills_acquired: uiData?.Skills_acquired || undefined
      });
    }
  }, [uiData, reset]);
  useEffect(() => {
    setValue('Skills_acquired', skillSets);
    //   const ValidationError = async () => await trigger('correctMatches');
    //   ValidationError();
  }, [skillSets]);
  const handleSkillChange = (id: string, value: string) => {
    setSkillSets(
      skillSets.map(skillSet =>
        skillSet.id === id
          ? {...skillSet, CourseLearningObjectiveType_id: value}
          : skillSet
      )
    );
  };

  // const handleProficiencyChange = (id: string, value: string) => {
  //   setSkillSets(
  //     skillSets.map(skillSet =>
  //       skillSet.id === id ? {...skillSet, rating_out_of_5: value} : skillSet
  //     )
  //   );
  // };

  const addSkillSet = () => {
    setSkillSets(prevChoices => [
      ...prevChoices,
      {
        id: uuidv4(),
        CourseLearningObjectiveType_id: '',
        rating_out_of_5: 1
      }
    ]);
  };
  const deleteSkillSet = async (id: string) => {
    if (setSkillSets && skillSets) {
      const valuess = skillSets.filter(
        (choice: {id: string}) => choice.id !== id
      );
      setSkillSets(valuess);
    }
    //   const correctChoice = watch('correctMatches');
    //   if (string(correctChoice) === id) {
    //     setValue('correctMatches', 1);
    //   }
    await trigger('Skills_acquired');
  };
  const saveForm = () => {
    // Handle the form submission event here
    // For example, sending data to a server or logging to the console
    console.log({skillSets});
  };
  const onSubmit = async (fieldsData: any) => {
    try {
      setIsSubmitting(true);
      const {sql_datas} = props.formdata['create_sql'];
      fieldsData.Skills_acquired = fieldsData?.Skills_acquired
        ? JSON.stringify(fieldsData.Skills_acquired)
        : '[]';
      fieldsData.CourseOverview_id = uniqueid;
      fieldsData.course_overview_id = uniqueid;
      let Operation = 'INSERT';
      if (uiData?.PreRequisitId) {
        fieldsData.id = uiData?.PreRequisitId;
        Operation = 'UPDATE';
      }
      const datas: any = {
        sql_data: sql_datas,
        data: fieldsData,
        operation: Operation
      };
      const encryptRequestDatas = EncryptUtils.encryptedData(datas);
      const results = await getLMSResultsData(applicationName, 'LMS', {
        tk: encryptRequestDatas
      });
      const transformedResult = results.data.map((item: any) => {
        return {
          ...item
          // Add any additional transformations here if needed
        };
      });
      setUiData(transformedResult[0]);
      setIsSubmitting(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsSubmitting(false);
    }
  };
  const renderActions = (
    <>
      <Grid
        sx={{
          display: 'flex',
          margin: '1vh,0',
          py: '1vh',
          justifyContent: 'flex-end'
        }}>
        <Grid xs={12} md={4}>
          {/* <LoadingButton
            type="reset"
            variant="outlined"
            // onClick={onReset}
            // loading={isSubmitting}
            // disabled={searchLoader ? true : false}
            sx={{
              mr: 2,
              backgroundColor: theme.palette.primary.light
            }}>
            {t('PRIVATE.COMMON.CLEAR', {ns: 'private'})}
          </LoadingButton> */}
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{
              mr: 2,
              backgroundColor: theme.palette.primary.dark
            }}
            // onClick={(e: React.MouseEvent<HTMLElement>) => {
            //   e.preventDefault();
            //   handleSubmit(onSubmit)();
            //   if (setExpanded) {
            //     setExpanded(false);
            //   }
            // }}
          >
            {t('PRIVATE.COMMON.SAVE', {ns: 'private'})}
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );

  return isPageLoading ? (
    <Stack
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'60vh'}>
      <LoadingScreen />
    </Stack>
  ) : (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Container
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '100% !important',
          py: '3vh'
        }}>
        <Grid xs={12} rowSpacing={1} marginBottom="3vh">
          <h3>Prerequisite skills</h3>
          <p style={{fontSize: 'x-small', color: 'grey'}}>
            Enter the basic level skills needed to be able to understand and
            participate in this course
          </p>
          <Stack spacing={1.5} sx={{paddingTop: '2vh', marginBottom: '1.5vh'}}>
            <Grid
              container
              spacing={2}
              sx={{margin: '0 !important', width: '100% !important'}}>
              <Grid xs={8} md={6} display={'grid'}>
                <Controller
                  name="CourseLearningObjectiveType_id"
                  control={control}
                  render={({field: {onChange, value}}) => {
                    const Options: RequiredOptions[] = dropDownData.find(
                      (item: {Name: string; Values: any}) =>
                        item.Name === 'Prerequisite'
                    )?.Values;
                    return (
                      <RHFAutocomplete
                        name="CourseLearningObjectiveType_id"
                        placeholder="Select Skill"
                        options={Options as readonly RequiredOptions[]}
                        getOptionLabel={option =>
                          (option as RequiredOptions).label || ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        onChange={(event, selectedOption: any) => {
                          onChange(
                            selectedOption ? selectedOption.value : null
                          );
                          trigger('CourseLearningObjectiveType_id');
                          trigger('rating_out_of_5');
                        }}
                        value={
                          Options
                            ? Options.find(option => option.value === value) ||
                              null
                            : null
                        }
                        errors={!!errors['CourseLearningObjectiveType_id']}
                        renderOption={(props, option: RequiredOptions) => (
                          <li {...props} key={option.value}>
                            {option.label}
                          </li>
                        )}
                        sx={{mb: 0.25, width: '95%'}}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid xs={6} md={4}>
                <Controller
                  name={`rating_out_of_5`}
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <RHFAutocomplete
                      name="roles"
                      label="Select Role"
                      options={_roles as readonly Options[]}
                      getOptionLabel={option => (option as Options).label || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      onChange={(event, selectedOption: any) => {
                        onChange(selectedOption ? selectedOption.value : null);
                        trigger('rating_out_of_5');
                        trigger('CourseLearningObjectiveType_id');
                      }}
                      value={
                        _roles.find(option => option.value === value) || null
                      }
                      errors={!!errors['rating_out_of_5']}
                      renderOption={(props, option: Options) => (
                        <li {...props} key={option.value}>
                          {option.label}
                        </li>
                      )}
                      sx={{mb: 0.25, width: '95%'}}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Stack>
        </Grid>
        <Grid xs={12} rowSpacing={1}>
          <h3>Skills you will acquire</h3>
          <p style={{fontSize: 'x-small', color: 'grey', paddingBottom: '5px'}}>
            Define the proficiency the learner will acquire from this course
          </p>
          {skillSets &&
            skillSets.map((skillSet, index) => (
              <Grid
                key={skillSet.id}
                container
                spacing={2}
                sx={{margin: '0 !important', width: '100% !important'}}>
                <Grid xs={8} md={6} pb={1} display={'grid'}>
                  <Controller
                    name={`Skills_acquired.${index}.CourseLearningObjectiveType_id`}
                    control={control}
                    render={({field: {onChange, value}}) => {
                      const Options: RequiredOptions[] = dropDownData.find(
                        (item: {Name: string; Values: any}) =>
                          item.Name === 'SkillsAcquired'
                      )?.Values;
                      return (
                        <RHFAutocomplete
                          name={`Skills_acquired.${index}.CourseLearningObjectiveType_id`}
                          label="Select Skill"
                          autoHighlight
                          options={Options as readonly RequiredOptions[]}
                          getOptionLabel={option =>
                            (option as RequiredOptions).label || ''
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          onChange={(event, selectedOption: any) => {
                            const updatedChoices = skillSets.map(item =>
                              item.id === skillSet.id
                                ? {
                                    ...item,
                                    CourseLearningObjectiveType_id:
                                      selectedOption?.value || null
                                  }
                                : item
                            );
                            if (setSkillSets) {
                              setSkillSets(updatedChoices);
                            }
                            onChange(
                              selectedOption ? selectedOption.value : null
                            );
                            trigger('Skills_acquired');
                          }}
                          value={(() => {
                            return Options
                              ? Options.find(
                                  option => option.value === value
                                ) || null
                              : null;
                          })()}
                          errors={
                            !!errors.Skills_acquired?.[index]
                              ?.CourseLearningObjectiveType_id
                          }
                          renderOption={(props, option: RequiredOptions) => (
                            <li {...props} key={option.value}>
                              {option.label}
                            </li>
                          )}
                          sx={{mb: 0.25, width: '95%'}}
                        />
                      );
                    }}
                  />
                </Grid>
                <Grid xs={6} md={4} display={'grid'}>
                  <Controller
                    name={`Skills_acquired.${index}.rating_out_of_5`}
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <RHFAutocomplete
                        name={`Skills_acquired.${index}.rating_out_of_5`}
                        label="Select Skill"
                        autoHighlight
                        options={_roles as readonly Options[]}
                        getOptionLabel={option =>
                          (option as Options).label || ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        onChange={(event, selectedOption: any) => {
                          const updatedChoices = skillSets.map(item =>
                            item.id === skillSet.id
                              ? {
                                  ...item,
                                  rating_out_of_5: selectedOption?.value || null
                                }
                              : item
                          );
                          if (setSkillSets) {
                            setSkillSets(updatedChoices);
                          }
                          onChange(
                            selectedOption ? selectedOption.value : null
                          );
                          trigger('Skills_acquired');
                        }}
                        value={
                          _roles.find(option => option.value === value) || null
                        }
                        errors={
                          !!errors.Skills_acquired?.[index]?.rating_out_of_5
                        }
                        renderOption={(props, option: Options) => (
                          <li {...props} key={option.value}>
                            {option.label}
                          </li>
                        )}
                      />
                    )}
                  />
                </Grid>

                <IconButton
                  onClick={() => {
                    deleteSkillSet(skillSet.id);
                  }}
                  color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            ))}
          <LoadingButton
            variant="contained"
            size="medium"
            onClick={() => addSkillSet()}
            sx={{backgroundColor: theme.palette.info.dark, marginTop: 0.5}}>
            {'+ Skill Set'}
          </LoadingButton>
        </Grid>
        {renderActions}
      </Container>
    </form>
  );
};

export default LearningObjectivesForm;
